import { z } from "zod";

/**
 * Shared Zod sub-schemas for per-parent override fields.
 *
 * Used by both SubgroupReference and ModifierGroupReference schemas
 * to avoid duplication of identical visibility, availability, and
 * scheduling override validation.
 */

// ── Availability ──

export const AvailabilityOverrideSchema = z
  .object({
    dineIn: z.boolean().default(true).readonly(),
    takeout: z.boolean().default(true).readonly(),
    delivery: z.boolean().default(true).readonly(),
    kiosk: z.boolean().default(true).readonly(),
    online: z.boolean().default(true).readonly(),
    partners: z.boolean().default(true).readonly(),
  })
  .strict();

// ── Date Range ──

export const DateRangeOverrideSchema = z
  .object({
    isActive: z.boolean().default(false).readonly(),
    startDate: z.date().nullable().optional().readonly(),
    endDate: z.date().nullable().optional().readonly(),
    displayMessage: z.string().nullable().optional().readonly(),
  })
  .strict();

// ── Time Windows ──

export const timeHHMMOverrideSchema = z
  .string()
  .regex(
    /^([01]\d|2[0-3]):([0-5]\d)$/,
    "Invalid time format, expected HH:MM",
  );

export const TimeWindowOverrideSchema = z
  .object({
    startTime: timeHHMMOverrideSchema.readonly(),
    endTime: timeHHMMOverrideSchema.readonly(),
    isActive: z.boolean().default(true).readonly(),
  })
  .strict();

// ── Days Available ──

export const DayScheduleOverrideSchema = z
  .object({
    isAvailable: z.boolean().readonly(),
    windows: z.array(TimeWindowOverrideSchema).readonly(),
  })
  .strict();

/** TOTAL — all seven keys required. Mirrors `PerDaySchema` in ./MenuGroup. */
export const PerDayOverrideSchema = z
  .object({
    sunday: DayScheduleOverrideSchema,
    monday: DayScheduleOverrideSchema,
    tuesday: DayScheduleOverrideSchema,
    wednesday: DayScheduleOverrideSchema,
    thursday: DayScheduleOverrideSchema,
    friday: DayScheduleOverrideSchema,
    saturday: DayScheduleOverrideSchema,
  })
  .strict();

export const DaysAvailableOverrideSchema = z
  .object({
    isActive: z.boolean().default(false).readonly(),
    sunday: z.boolean().default(true).readonly(),
    monday: z.boolean().default(true).readonly(),
    tuesday: z.boolean().default(true).readonly(),
    wednesday: z.boolean().default(true).readonly(),
    thursday: z.boolean().default(true).readonly(),
    friday: z.boolean().default(true).readonly(),
    saturday: z.boolean().default(true).readonly(),
    timeWindows: z.record(z.string(), TimeWindowOverrideSchema).default({}).readonly(),
    /**
     * Must widen in lockstep with `DaysAvailableSchema` in ./MenuGroup. Both are
     * `.strict()`, and `SubgroupReference`/`ModifierGroupReference` embed THIS
     * one inside the parent menu-group document. If only one widens, a subgroup
     * override carrying `perDay` fails `unrecognized_keys` and bricks EVERY
     * `updateMenuGroup` on the parent — including the narrow `{items}` write
     * from ItemModal.
     */
    perDay: PerDayOverrideSchema.optional().readonly(),
    startTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Invalid time format, expected HH:MM")
      .nullable()
      .optional()
      .readonly(),
    endTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Invalid time format, expected HH:MM")
      .nullable()
      .optional()
      .readonly(),
  })
  .strict();

// ── Seasonal Availability ──

export const SeasonalAvailabilityOverrideSchema = z
  .object({
    isActive: z.boolean().default(false).readonly(),
    startMonth: z.number().int().min(1).max(12).nullable().optional().readonly(),
    startDay: z.number().int().min(1).max(31).nullable().optional().readonly(),
    endMonth: z.number().int().min(1).max(12).nullable().optional().readonly(),
    endDay: z.number().int().min(1).max(31).nullable().optional().readonly(),
    displayMessage: z.string().nullable().optional().readonly(),
  })
  .strict();

// ── Pricing ──

export const GroupSizePriceOverrideSchema = z
  .object({
    name: z.string().min(1, "Size name is required").readonly(),
    price: z.number().min(0, "Price must be >= 0").readonly(),
  })
  .strict();
